import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ColumnMode } from '@swimlane/ngx-datatable';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import * as dayjs from 'dayjs';
import { UtilsService } from 'src/app/services/utils/utils.service';
import {
	CallerEffortRow,
	CampaignEffortRow,
	CampaignEffortService,
	CallerTimeRow,
	DispoRow,
} from '../../services/campaign-effort/campaign-effort.service';

type Tab = 'campaign' | 'caller';
type Scope = 'campaign' | 'caller';

/**
 * Campaign Effort — the internal admin cockpit (Port Spec). Sibling of Callers Report /
 * Dispos Report under Quality Control; matches their layout (date-range filter, cards,
 * datatables). Composes the existing dispo + caller-report endpoints via CampaignEffortService
 * and shows: By Campaign, By Caller, and a per-lead Effort Receipt with dispos-to-secure.
 */
@Component({
	selector: 'app-campaign-effort',
	templateUrl: './campaign-effort.component.html',
	styleUrls: ['./campaign-effort.component.scss'],
})
export class CampaignEffortComponent implements OnInit {
	@ViewChild('receiptModal') receiptModalRef!: TemplateRef<any>;
	ColumnMode = ColumnMode;

	dateRange_: any;
	fromDate = '';
	toDate = '';
	loading = false;

	tab: Tab = 'campaign';

	// Raw range data (fetched once per date range).
	dispos: DispoRow[] = [];
	callerTimes: CallerTimeRow[] = [];

	// Per-endpoint failure flags — drive partial rendering + "unavailable" markers.
	disposFailed = false;
	callerTimesFailed = false;

	// Aggregations.
	campaignRows: CampaignEffortRow[] = [];
	callerRows: CallerEffortRow[] = [];

	// KPI roll-ups.
	kpiCampaign = { leads: 0, worked: 0, talkMs: 0, workedPerLead: 0 };
	kpiCaller = { leads: 0, calls: 0, talkMs: 0, avgConv: 0 };

	topProducer = '';
	bestConverter = '';

	// Drill state.
	drillCampaign: string | null = null;
	drillCaller: string | null = null;
	drillLeads: DispoRow[] = [];

	// Receipt state.
	selectedLead: DispoRow | null = null;
	receiptScope: Scope = 'campaign';
	leadDetails: any = null;
	dtsCampaign = 0;
	dtsCaller = 0;

	talkFailed = false;

	constructor(
		public utils: UtilsService,
		private effortSrv: CampaignEffortService,
		private modalSrv: NgbModal,
	) {
		this.fromDate = dayjs().subtract(1, 'month').format('YYYY-MM-DD') + ' 00:00:00';
		this.toDate = dayjs().format('YYYY-MM-DD') + ' 23:59:59';
		this.dateRange_ = { dateStart: dayjs().subtract(1, 'month'), dateEnd: dayjs() };
	}

	ngOnInit(): void {
		this.getReport();
	}

	dateSelected(dates: any): void {
		this.fromDate = dayjs(dates.dateStart).format('YYYY-MM-DD') + ' 00:00:00';
		this.toDate = dayjs(dates.dateEnd).format('YYYY-MM-DD') + ' 23:59:59';
		this.getReport();
	}

	getReport(): void {
		this.loading = true;
		this.clearDrill();
		this.effortSrv.loadRange(this.fromDate, this.toDate).subscribe({
			next: (data) => {
				this.talkFailed = data.talkFailed;
				this.dispos = data.dispos;
				this.callerTimes = data.callerTimes;
				this.disposFailed = data.disposFailed;
				this.callerTimesFailed = data.callerTimesFailed;
				// By Campaign is dispos-derived (Talk* naturally shows "—" when caller times are 0).
				this.campaignRows = this.effortSrv.byCampaign(this.dispos, this.callerTimes, data.talk);
				// By Caller: if the caller-times endpoint failed, still render dispos/hot/leads from
				// dispos alone (Calls*/Talk* marked unavailable); otherwise use the full aggregation.
				this.callerRows = this.callerTimesFailed
					? this.effortSrv.byCallerFromDispos(this.dispos)
					: this.effortSrv.byCaller(this.dispos, this.callerTimes);
				this.computeKpis();
				this.loading = false;
			},
			// Safety net only — per-call catchError means this rarely fires now.
			error: () => {
				this.dispos = [];
				this.callerTimes = [];
				this.disposFailed = true;
				this.callerTimesFailed = true;
				this.campaignRows = [];
				this.callerRows = [];
				this.computeKpis();
				this.loading = false;
				this.talkFailed = true;
			},
		});
	}

	setTab(tab: Tab): void {
		this.tab = tab;
		this.clearDrill();
	}

	private computeKpis(): void {
		const leads = this.campaignRows.reduce((s, r) => s + r.leads, 0);
		const worked = this.dispos.length;
		const talkMs = this.campaignRows.reduce((s, r) => s + r.talkMs, 0);
		this.kpiCampaign = { leads, worked, talkMs, workedPerLead: leads ? worked / leads : 0 };

		const cLeads = this.callerRows.reduce((s, r) => s + r.leads, 0);
		const cDispos = this.callerRows.reduce((s, r) => s + r.dispos, 0);
		const cCalls = this.callerRows.reduce((s, r) => s + r.calls, 0);
		const cTalk = this.callerRows.reduce((s, r) => s + r.talkMs, 0);
		this.kpiCaller = { leads: cLeads, calls: cCalls, talkMs: cTalk, avgConv: cDispos ? (cLeads / cDispos) * 100 : 0 };

		this.topProducer = this.callerRows[0]?.name ?? '';
		this.bestConverter = this.callerRows.reduce(
			(best, r) => (r.conv > (best?.conv ?? -1) ? r : best),
			null as CallerEffortRow | null,
		)?.name ?? '';
	}

	// ── Drill ─────────────────────────────────────────────────────────────────
	openCampaign(name: string): void {
		this.drillCampaign = name;
		this.drillCaller = null;
		this.drillLeads = this.effortSrv.leadsForCampaign(this.dispos, name);
	}

	openCaller(name: string): void {
		this.drillCaller = name;
		this.drillCampaign = null;
		this.drillLeads = this.effortSrv.leadsForCaller(this.dispos, name);
	}

	clearDrill(): void {
		this.drillCampaign = null;
		this.drillCaller = null;
		this.drillLeads = [];
	}

	// ── Effort Receipt ──────────────────────────────────────────────────────────
	openLead(lead: DispoRow, scope: Scope): void {
		this.selectedLead = lead;
		this.receiptScope = scope;
		this.leadDetails = null;
		this.dtsCampaign = this.effortSrv.disposToSecure(this.dispos, lead, false);
		this.dtsCaller = this.effortSrv.disposToSecure(this.dispos, lead, true);
		this.effortSrv.getDispoDetails(lead.id).subscribe({
			next: (data: any) => (this.leadDetails = data?.body ?? null),
			error: () => (this.leadDetails = null),
		});
		this.modalSrv.open(this.receiptModalRef, { size: 'lg', centered: true, scrollable: true });
	}

	toggleReceiptScope(): void {
		this.receiptScope = this.receiptScope === 'caller' ? 'campaign' : 'caller';
	}

	get dtsCurrent(): number {
		return this.receiptScope === 'caller' ? this.dtsCaller : this.dtsCampaign;
	}
	get dtsAlt(): number {
		return this.receiptScope === 'caller' ? this.dtsCampaign : this.dtsCaller;
	}

	// ── Display helpers ─────────────────────────────────────────────────────────
	talk(ms: number): string {
		return ms ? this.utils.formatMiliSeconds(ms) : '—';
	}

	pct(part: number, whole: number): number {
		return whole ? (part / whole) * 100 : 0;
	}

	effortClass(score: number): string {
		if (score >= 85) return 'bg-success';
		if (score >= 60) return 'bg-warning';
		return 'bg-danger';
	}

	// ── Effort ring gauge (ports prototype TS:120-122 + scoreBand thresholds) ──
	readonly RING_C = 2 * Math.PI * 16; // r=16 circumference — matches the prototype ring
	ringDash(score: number): string {
		return `${(score / 100) * this.RING_C} ${this.RING_C}`;
	}
	/** Score-band colour: ≥85 green · ≥60 amber · else red (mirrors prototype scoreBand). */
	bandColor(score: number): string {
		if (score >= 85) return '#16A34A';
		if (score >= 60) return '#D97706';
		return '#DC2626';
	}

	/** Worked-per-lead threshold colour (ports prototype wplColor): ≤45 green · ≤70 amber · else red. */
	wplColor(workedPerLead: number): string {
		if (workedPerLead <= 45) return '#16A34A';
		if (workedPerLead <= 70) return '#D97706';
		return '#DC2626';
	}
	// Criteria labels for the qualification-proof placeholder (no data source yet — shown as pending, never ✓).
	qualificationCriteria: string[] = [
		'Decision-maker confirmed',
		'Agreed to meeting',
		'Timeframe within 90 days',
		'Budget authority',
	];

	/** Export PDF placeholder — button is present (matches mockup) but does NOT export.
	 *  Must never render the pending placeholders as a real, send-ready receipt. */
	exportReceiptPdf(): void {
		// TODO(Hiram: proper server-side, send-ready PDF of the Lead Effort Receipt).
		// Intentionally a no-op — NOT window.print() (that would export the placeholders as if real).
	}

	badgeColor(dispo: string): string {
		switch (this.effortSrv.bucketOf(dispo)) {
			case 'green': return 'bg-success';
			case 'yellow': return 'bg-warning';
			case 'blue': return 'bg-primary';
			case 'black': return 'bg-dark';
			default: return 'bg-secondary';
		}
	}

	initials(name: string): string {
		return (name || '')
			.split(/\s+/)
			.map((p) => p[0])
			.filter(Boolean)
			.slice(0, 2)
			.join('')
			.toUpperCase();
	}
}
